import type { ComponentProps, ReactNode } from 'react';
import { useId } from 'react';

import { cn } from '../../lib/cn';

/** CMS/Input, CMS/Select and the uppercase field label of Figma 54:3. */
export function Label({ className, ...props }: ComponentProps<'label'>) {
  return (
    <label
      className={cn('text-[12px] font-semibold tracking-wide text-muted uppercase', className)}
      {...props}
    />
  );
}

const control =
  'w-full rounded-lg border border-line bg-panel px-3 text-[13px] text-ink placeholder:text-muted/70 focus:border-pink focus:outline-none disabled:cursor-not-allowed disabled:bg-canvas';

/**
 * Leaving a field scrolls it back to the start of its value, so a long name reads from its first
 * word again instead of staying parked on the end where the caret was (UI review 2026-10-05).
 */
export function Input({ className, onBlur, ...props }: ComponentProps<'input'>) {
  return (
    <input
      className={cn(control, 'h-9', className)}
      onBlur={(event) => {
        event.currentTarget.scrollLeft = 0;
        onBlur?.(event);
      }}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return <textarea className={cn(control, 'min-h-20 py-2 leading-5', className)} {...props} />;
}

export function Select({ className, ...props }: ComponentProps<'select'>) {
  return <select className={cn(control, 'h-9 pr-8', className)} {...props} />;
}

/** Label + control + help/error, the layout every CMS form uses. */
export function Field({
  label,
  help,
  error,
  children,
  className,
}: {
  label: string;
  help?: ReactNode;
  error?: string;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children(id)}
      {error ? (
        <p className="text-[12px] text-danger" role="alert">
          {error}
        </p>
      ) : help ? (
        <p className="text-[12px] text-muted">{help}</p>
      ) : null}
    </div>
  );
}
